import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck, ShieldEllipsis } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../api';
import { AppShell } from '../ui/AppShell';
import { PageHeader } from '../ui/PageHeader';
import { SectionCard } from '../ui/components';
import { ActionQueueCard, MetricStrip } from '../ui/mobileOps';
import {
  Button,
  ErrorBanner,
  Pill,
  Spinner,
  TextInput,
} from '../ui/primitives';
import { useWhoami } from '../lib/auth';

export function AccountPage() {
  const who = useWhoami();
  const qc = useQueryClient();
  const nav = useNavigate();

  const logoutMut = useMutation({
    mutationFn: () => api.post<unknown>('/api/auth/logout'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['whoami'] });
      nav('/login', { replace: true });
    },
  });

  return (
    <AppShell
      title="Account"
      toolbar={
        <PageHeader
          section="settings"
          title="My account"
          subtitle="Password, PIN, and session controls for the current login."
          breadcrumb={[{ label: 'Settings', to: '/settings' }, { label: 'Account' }]}
        />
      }
    >
      {who.data?.state === 'authenticated' ? (
        <div className="space-y-4">
          <MetricStrip
            section="settings"
            items={[
              { label: 'Role', value: who.data.role === 'owner' ? 'Owner' : 'Boss', note: 'current session' },
              { label: 'PIN', value: who.data.hasPin ? 'Set' : 'Missing', note: who.data.hasPin ? 'destructive actions stay gated' : 'set one for safer approvals', tone: who.data.hasPin ? 'pos' : 'warn' },
              { label: 'Access', value: who.data.role === 'owner' ? 'Live' : 'View only', note: who.data.role === 'owner' ? 'can change data' : 'mutations are blocked' },
            ]}
          />

          <div className="grid gap-4 lg:grid-cols-[1.02fr_0.98fr]">
            <ActionQueueCard
              section="settings"
              eyebrow="Signed in"
              title={who.data.username}
              description={
                who.data.role === 'owner'
                  ? 'This login can change data, users, and settings. Keep both password and PIN strong because this is the highest-privilege account.'
                  : 'Boss access is designed for oversight. You can review every workflow safely without being able to change ledger, batch, cash, or match data.'
              }
              meta={
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <span>Role</span>
                    <span className="font-medium text-ink">{who.data.role}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span>PIN status</span>
                    <span className="font-medium text-ink">{who.data.hasPin ? 'Set' : 'Not set'}</span>
                  </div>
                  <div className="text-[12px] leading-5 text-ink-muted">
                    Password protects sign-in. PIN protects destructive and high-trust actions while you stay logged in.
                  </div>
                </div>
              }
              actions={
                <Button
                  variant="danger"
                  onClick={() => logoutMut.mutate()}
                  disabled={logoutMut.isPending}
                >
                  {logoutMut.isPending ? <Spinner /> : 'Sign out'}
                </Button>
              }
            />

            <SectionCard section="settings" title="Security checklist">
              <div className="space-y-3">
                <div className="rounded-[22px] border border-border/70 bg-surface-raised p-4">
                  <div className="flex items-start gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-[16px] bg-[var(--sec-settings-tint)] text-[var(--sec-settings)]">
                      <ShieldCheck className="h-[18px] w-[18px]" strokeWidth={2} />
                    </span>
                    <div>
                      <div className="text-small font-semibold text-ink">Password</div>
                      <div className="mt-1 text-[12px] leading-5 text-ink-muted">
                        Change this when the device or browser has been used by someone else, or after sharing a one-time reset password.
                      </div>
                    </div>
                  </div>
                </div>
                <div className="rounded-[22px] border border-border/70 bg-surface-raised p-4">
                  <div className="flex items-start gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-[16px] bg-[var(--sec-settings-tint)] text-[var(--sec-settings)]">
                      <ShieldEllipsis className="h-[18px] w-[18px]" strokeWidth={2} />
                    </span>
                    <div>
                      <div className="text-small font-semibold text-ink">PIN</div>
                      <div className="mt-1 text-[12px] leading-5 text-ink-muted">
                        The PIN creates a short unlock window for sensitive actions so you do not have to re-enter it for every single tap.
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <Pill tone={who.data.role === 'owner' ? 'amber' : 'neutral'}>
                    {who.data.role}
                  </Pill>
                  <Pill tone={who.data.hasPin ? 'green' : 'amber'}>
                    {who.data.hasPin ? 'PIN ready' : 'PIN missing'}
                  </Pill>
                </div>
              </div>
            </SectionCard>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <ChangePasswordCard />
            <ChangePinCard hasPin={who.data.hasPin} />
          </div>
        </div>
      ) : (
        <SectionCard section="settings" title="Account unavailable">
          <ErrorBanner>Not authenticated.</ErrorBanner>
        </SectionCard>
      )}
    </AppShell>
  );
}

function ChangePasswordCard() {
  const [currentPassword, setCurrent] = useState('');
  const [newPassword, setNew] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  const mut = useMutation({
    mutationFn: async () => {
      if (newPassword !== confirm) throw new Error('Passwords do not match.');
      await api.post('/api/auth/change-password', { currentPassword, newPassword });
    },
    onSuccess: () => {
      setError(null);
      setOk(true);
      setCurrent('');
      setNew('');
      setConfirm('');
    },
    onError: (err) => {
      setOk(false);
      setError(err instanceof Error ? err.message : String(err));
    },
  });

  return (
    <SectionCard section="settings" title="Change password">
      <p className="mb-4 text-[13px] leading-5 text-ink-muted">
        Update the sign-in password for this user. Use something unique and do not reuse a password you already use elsewhere.
      </p>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          setOk(false);
          mut.mutate();
        }}
      >
        <TextInput
          label="Current password"
          type="password"
          autoComplete="current-password"
          value={currentPassword}
          onChange={(e) => setCurrent(e.target.value)}
          name="current-password"
        />
        <TextInput
          label="New password"
          type="password"
          autoComplete="new-password"
          value={newPassword}
          onChange={(e) => setNew(e.target.value)}
          name="new-password"
        />
        <TextInput
          label="Confirm new password"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          name="confirm-password"
        />
        {error && <ErrorBanner>{error}</ErrorBanner>}
        {ok && <p className="text-caption text-pos">Password changed.</p>}
        <Button type="submit" disabled={mut.isPending}>
          {mut.isPending ? <Spinner /> : 'Save new password'}
        </Button>
      </form>
    </SectionCard>
  );
}

function ChangePinCard({ hasPin }: { hasPin: boolean }) {
  const qc = useQueryClient();
  const [currentPin, setCurrent] = useState('');
  const [newPin, setNew] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  const mut = useMutation({
    mutationFn: async () => {
      if (newPin !== confirm) throw new Error('PINs do not match.');
      const payload: { currentPin?: string; newPin: string } = { newPin };
      if (hasPin) payload.currentPin = currentPin;
      await api.post('/api/auth/change-pin', payload);
    },
    onSuccess: () => {
      setError(null);
      setOk(true);
      setCurrent('');
      setNew('');
      setConfirm('');
      void qc.invalidateQueries({ queryKey: ['whoami'] });
    },
    onError: (err) => {
      setOk(false);
      if (err instanceof ApiError) setError(err.message);
      else setError(err instanceof Error ? err.message : String(err));
    },
  });

  return (
    <SectionCard section="settings" title={hasPin ? 'Change PIN' : 'Set PIN'}>
      <p className="mb-4 text-[13px] leading-5 text-ink-muted">
        {hasPin
          ? 'Replace the current PIN used for high-trust actions.'
          : 'Create a PIN now so destructive actions stay protected even when the browser session is already open.'}
      </p>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          setOk(false);
          mut.mutate();
        }}
      >
        {hasPin && (
          <TextInput
            label="Current PIN"
            type="password"
            value={currentPin}
            onChange={(e) => setCurrent(e.target.value)}
            name="current-pin"
          />
        )}
        <TextInput
          label="New PIN"
          type="password"
          value={newPin}
          onChange={(e) => setNew(e.target.value)}
          name="new-pin"
          hint="4 to 64 characters. Keep it memorable but not obvious."
        />
        <TextInput
          label="Confirm new PIN"
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          name="confirm-pin"
        />
        {error && <ErrorBanner>{error}</ErrorBanner>}
        {ok && <p className="text-caption text-pos">PIN updated.</p>}
        <Button type="submit" disabled={mut.isPending}>
          {mut.isPending ? <Spinner /> : hasPin ? 'Save new PIN' : 'Create PIN'}
        </Button>
      </form>
    </SectionCard>
  );
}
